import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ServiceRequest } from '@/types/shared/serviceRequest';
import {
  DollarSign,
  Clock,
  Calendar,
  Award,
  Shield,
  Upload,
  X,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react';

interface VendorProfile {
  id: string;
  name: string;
  rating: number;
  reviews: number;
  certifications: string[];
  specialties: string[];
  responseTime: string;
  completedJobs: number;
  guarantees: string[];
}

interface VendorBid {
  vendorId: string;
  vendorName: string;
  vendorRating: number;
  vendorReviews: number;
  certifications: string[];
  serviceRequestId: string;
  submittedAt: string;
  status: 'pending' | 'accepted' | 'rejected';
  estimatedCost: string;
  estimatedDuration: string;
  availability: string;
  message: string;
  guarantees: string[];
  customGuarantees: string;
  emergencyService: boolean;
  materialsIncluded: boolean;
  followUpIncluded: boolean;
  portfolioItems: File[];
  responseTime: string;
  terms: {
    depositRequired: boolean;
    depositAmount: string;
    paymentTerms: string;
    cancellationPolicy: string;
  };
}

interface VendorBiddingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitBid: (bid: VendorBid) => void;
  serviceRequest: ServiceRequest | null;
  vendorProfile: VendorProfile;
}

const mockVendorProfile: VendorProfile = {
  id: 'vendor-123',
  name: 'ProFix Services',
  rating: 4.8,
  reviews: 127,
  certifications: ['EPA Certified', 'Licensed Contractor', 'Insured & Bonded'],
  specialties: ['Appliance Repair', 'HVAC', 'Plumbing'],
  responseTime: '< 4 hours',
  completedJobs: 89,
  guarantees: ['90-day parts warranty', '30-day labor warranty', 'Satisfaction guarantee'],
};

export function VendorBiddingModal({
  isOpen,
  onClose,
  onSubmitBid,
  serviceRequest,
  vendorProfile = mockVendorProfile,
}: VendorBiddingModalProps) {
  const [bidData, setBidData] = useState({
    estimatedCost: '',
    estimatedDuration: '',
    availability: '',
    message: '',
    guarantees: vendorProfile.guarantees,
    customGuarantees: '',
    emergencyService: false,
    materialsIncluded: true,
    followUpIncluded: false,
    portfolioItems: [] as File[],
    responseTime: vendorProfile.responseTime,
    terms: {
      depositRequired: false,
      depositAmount: '',
      paymentTerms: 'completion',
      cancellationPolicy: 'standard',
    },
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Early return if serviceRequest is null
  if (!serviceRequest) {
    return null;
  }

  const handleInputChange = (field: string, value: string | boolean | File[]) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setBidData((prev) => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof typeof prev] as Record<string, unknown>),
          [child]: value,
        },
      }));
    } else {
      setBidData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setBidData((prev) => ({
      ...prev,
      portfolioItems: [...prev.portfolioItems, ...files].slice(0, 3),
    }));
  };

  const removeFile = (index: number) => {
    setBidData((prev) => ({
      ...prev,
      portfolioItems: prev.portfolioItems.filter((_, i) => i !== index),
    }));
  };

  const validateBid = () => {
    const errors: string[] = [];

    if (!bidData.estimatedCost || isNaN(Number(bidData.estimatedCost))) {
      errors.push('Please provide a valid estimated cost');
    }

    if (!bidData.estimatedDuration) {
      errors.push('Please provide an estimated duration');
    }

    if (!bidData.availability) {
      errors.push('Please specify your availability');
    }

    if (!bidData.message || bidData.message.length < 50) {
      errors.push('Please provide a detailed message (at least 50 characters)');
    }

    // Check if bid is within budget
    if (serviceRequest.budget && Number(bidData.estimatedCost) > serviceRequest.budget.max) {
      errors.push(
        `Your bid (${bidData.estimatedCost}) exceeds the client's budget (${serviceRequest.budget.max})`,
      );
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSubmit = () => {
    if (!validateBid()) return;

    const finalBid = {
      ...bidData,
      vendorId: vendorProfile.id,
      vendorName: vendorProfile.name,
      vendorRating: vendorProfile.rating,
      vendorReviews: vendorProfile.reviews,
      certifications: vendorProfile.certifications,
      serviceRequestId: serviceRequest.id,
      submittedAt: new Date().toISOString(),
      status: 'pending' as const,
    };

    onSubmitBid(finalBid);
    onClose();
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center space-x-4 mb-6">
      {[1, 2, 3].map((step) => (
        <div key={step} className="flex items-center">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step <= currentStep
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            {step < currentStep ? <CheckCircle className="w-4 h-4" /> : step}
          </div>
          {step < 3 && (
            <div className={`w-12 h-0.5 mx-2 ${step < currentStep ? 'bg-primary' : 'bg-muted'}`} />
          )}
        </div>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Service Request Overview</h3>
        <Card>
          <CardContent className="p-4">
            <h4 className="font-medium mb-2">{serviceRequest.title}</h4>
            <p className="text-sm text-muted-foreground mb-3">{serviceRequest.description}</p>
            <div className="flex flex-wrap gap-2 mb-3">
              <Badge variant="outline">{serviceRequest.category}</Badge>
              <Badge variant={serviceRequest.priority === 'urgent' ? 'destructive' : 'secondary'}>
                {serviceRequest.priority} priority
              </Badge>
            </div>
            <div className="text-sm text-muted-foreground">
              <div>
                Location:{' '}
                {serviceRequest.location.building && `${serviceRequest.location.building}`}
                {serviceRequest.location.unit && ` - Unit ${serviceRequest.location.unit}`}
              </div>
              {serviceRequest.budget && (
                <div>
                  Budget: Up to ${serviceRequest.budget.max} {serviceRequest.budget.currency}
                </div>
              )}
              {serviceRequest.timeline?.deadline && (
                <div>
                  Deadline: {new Date(serviceRequest.timeline.deadline).toLocaleDateString()}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Your Pricing & Timeline</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="estimatedCost">Estimated Cost *</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  id="estimatedCost"
                  type="number"
                  placeholder="Enter amount"
                  className="pl-9"
                  value={bidData.estimatedCost}
                  onChange={(e) => handleInputChange('estimatedCost', e.target.value)}
                />
              </div>
              {serviceRequest.budget && (
                <p className="text-xs text-muted-foreground mt-1">
                  Client budget: ${serviceRequest.budget.max}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="estimatedDuration">Estimated Duration *</Label>
              <Select
                value={bidData.estimatedDuration}
                onValueChange={(value) => handleInputChange('estimatedDuration', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select duration" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1-2 hours">1-2 hours</SelectItem>
                  <SelectItem value="2-4 hours">2-4 hours</SelectItem>
                  <SelectItem value="4-6 hours">4-6 hours</SelectItem>
                  <SelectItem value="1 day">1 day</SelectItem>
                  <SelectItem value="2-3 days">2-3 days</SelectItem>
                  <SelectItem value="1 week">1 week</SelectItem>
                  <SelectItem value="custom">Custom duration</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="availability">Your Availability *</Label>
            <Input
              id="availability"
              placeholder="e.g., Available today after 2 PM, Tomorrow morning 9-12"
              value={bidData.availability}
              onChange={(e) => handleInputChange('availability', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <Switch
                id="emergencyService"
                checked={bidData.emergencyService}
                onCheckedChange={(checked) => handleInputChange('emergencyService', checked)}
              />
              <Label htmlFor="emergencyService">Emergency service available</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="materialsIncluded"
                checked={bidData.materialsIncluded}
                onCheckedChange={(checked) => handleInputChange('materialsIncluded', checked)}
              />
              <Label htmlFor="materialsIncluded">Materials included in price</Label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Your Proposal Message</h3>
        <div>
          <Label htmlFor="message">Detailed Message *</Label>
          <Textarea
            id="message"
            placeholder="Explain your approach, relevant experience, why you're the best choice for this job..."
            rows={6}
            value={bidData.message}
            onChange={(e) => handleInputChange('message', e.target.value)}
          />
          <p className="text-xs text-muted-foreground mt-1">
            {bidData.message.length}/50 characters minimum
          </p>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Guarantees & Warranties</h3>
        <div className="space-y-3">
          <div>
            <Label className="font-medium">Included Guarantees</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {bidData.guarantees.map((guarantee, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  <Shield className="w-3 h-3 mr-1" />
                  {guarantee}
                </Badge>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="customGuarantees">Additional Guarantees (Optional)</Label>
            <Textarea
              id="customGuarantees"
              placeholder="Any additional guarantees or warranties you'd like to offer..."
              rows={3}
              value={bidData.customGuarantees}
              onChange={(e) => handleInputChange('customGuarantees', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Portfolio Items (Optional)</h3>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground mb-2">
            Upload photos of similar work you've completed
          </p>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
            id="portfolio-upload"
          />
          <Button variant="outline" size="sm" asChild>
            <label htmlFor="portfolio-upload" className="cursor-pointer">
              Choose Photos
            </label>
          </Button>
        </div>

        {bidData.portfolioItems.length > 0 && (
          <div className="grid grid-cols-3 gap-4 mt-4">
            {bidData.portfolioItems.map((file, index) => (
              <div key={index} className="relative">
                <img
                  src={URL.createObjectURL(file)}
                  alt={`Portfolio ${index + 1}`}
                  className="w-full h-24 object-cover rounded-lg"
                />
                <Button
                  variant="destructive"
                  size="sm"
                  className="absolute -top-2 -right-2 w-6 h-6 p-0"
                  onClick={() => removeFile(index)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Terms & Conditions</h3>
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="depositRequired"
              checked={bidData.terms.depositRequired}
              onCheckedChange={(checked) => handleInputChange('terms.depositRequired', checked)}
            />
            <Label htmlFor="depositRequired">Deposit required before starting work</Label>
          </div>

          {bidData.terms.depositRequired && (
            <div className="ml-6">
              <Label htmlFor="depositAmount">Deposit Amount (%)</Label>
              <Input
                id="depositAmount"
                type="number"
                placeholder="e.g., 25"
                value={bidData.terms.depositAmount}
                onChange={(e) => handleInputChange('terms.depositAmount', e.target.value)}
                className="w-32"
              />
            </div>
          )}

          <div>
            <Label htmlFor="paymentTerms">Payment Terms</Label>
            <Select
              value={bidData.terms.paymentTerms}
              onValueChange={(value) => handleInputChange('terms.paymentTerms', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="completion">Payment due upon completion</SelectItem>
                <SelectItem value="50-50">50% upfront, 50% on completion</SelectItem>
                <SelectItem value="weekly">Weekly payments</SelectItem>
                <SelectItem value="milestone">Milestone-based payments</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="cancellationPolicy">Cancellation Policy</Label>
            <Select
              value={bidData.terms.cancellationPolicy}
              onValueChange={(value) => handleInputChange('terms.cancellationPolicy', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard (24 hours notice)</SelectItem>
                <SelectItem value="flexible">Flexible (same day cancellation OK)</SelectItem>
                <SelectItem value="strict">Strict (48 hours notice required)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Bid Summary</h3>
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex justify-between">
              <span className="font-medium">Estimated Cost:</span>
              <span className="text-lg font-bold text-green-600">${bidData.estimatedCost}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Duration:</span>
              <span>{bidData.estimatedDuration}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Availability:</span>
              <span>{bidData.availability}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Response Time:</span>
              <span>{bidData.responseTime}</span>
            </div>
            {bidData.emergencyService && (
              <div className="flex items-center gap-2 text-sm text-orange-600">
                <AlertTriangle className="w-4 h-4" />
                Emergency service available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {validationErrors.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <ul className="list-disc list-inside">
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Submit Your Bid</DialogTitle>
          <DialogDescription>
            Provide a detailed proposal for this service request
          </DialogDescription>
        </DialogHeader>

        {renderStepIndicator()}

        <div className="space-y-6">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
        </div>

        <div className="flex justify-between pt-6 border-t">
          <div>
            {currentStep > 1 && (
              <Button variant="outline" onClick={() => setCurrentStep(currentStep - 1)}>
                Back
              </Button>
            )}
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            {currentStep < 3 ? (
              <Button
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={
                  currentStep === 1 &&
                  (!bidData.estimatedCost || !bidData.estimatedDuration || !bidData.availability)
                }
              >
                Next
              </Button>
            ) : (
              <Button onClick={handleSubmit}>Submit Bid</Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
